import { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { TextHeadingWithBody } from "@ui-kit/TextHeadingWithBody/TextHeadingWithBody";
import { Text } from "@ui-kit/Text/Text";
import { TabSingleSelect } from "@ui-kit/TabButtonGroup/TabSingleSelect";
import { useTabSingleSelect } from "@ui-kit/TabButtonGroup/useTabSelect";
import { ModalNavFooter } from "@ui-kit/Modal/ModalNavFooter";
import { useAppDispatch, useAppSelector } from "@store/store";
import {
  setCurrentStep,
  setGroupComposition,
  selectGroupComposition,
} from "@store/tripBuilder/tripBuilderReducer";
import { TabGroup } from "@ui-kit/TabButtonGroup/Tab";
import { GroupComposition } from "@dto/TripBuilder";

const TripBuilderGroupComposition: FunctionComponent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const groupComposition = useAppSelector(selectGroupComposition);
  const initialTravelingWith =
    groupComposition === "family" ? "group" : groupComposition;
  const initialHasKids =
    groupComposition === "family"
      ? true
      : groupComposition === "group"
        ? false
        : null;

  type TravelingWith = Exclude<GroupComposition, "family">;
  const travelingWithTabs: TabGroup<TravelingWith> = [
    {
      label: t("screens.tripBuilder.groupComposition.options.solo"),
      value: "solo",
    },
    {
      label: t("screens.tripBuilder.groupComposition.options.couple"),
      value: "couple",
    },
    {
      label: t("screens.tripBuilder.groupComposition.options.group"),
      value: "group",
    },
  ];

  const kidsOptions: TabGroup<boolean> = [
    {
      label: t("common.yes"),
      value: true,
    },
    {
      label: t("common.no"),
      value: false,
    },
  ];

  const setHasKids = useCallback(
    (selectedHasKids: boolean) => {
      dispatch(setGroupComposition(selectedHasKids ? "family" : "group"));
    },
    [dispatch],
  );
  const { activeTab: hasKids, onTabSelect: handleHasKidsSelect } =
    useTabSingleSelect<boolean>({
      initialTab: initialHasKids,
      onTabSelect: setHasKids,
    });

  const setTravelingWith = useCallback(
    (travelingWith: TravelingWith) => {
      if (travelingWith === "group") {
        if (hasKids === null) {
          dispatch(setGroupComposition(null));
        } else {
          dispatch(setGroupComposition(hasKids ? "family" : "group"));
        }
      } else if (travelingWith !== null) {
        dispatch(setGroupComposition(travelingWith));
      }
    },
    [dispatch, hasKids],
  );
  const { activeTab: travelingWith, onTabSelect: handleTravelingWithSelect } =
    useTabSingleSelect<TravelingWith>({
      initialTab: initialTravelingWith,
      onTabSelect: setTravelingWith,
    });

  const goBack = useCallback(() => {
    dispatch(setCurrentStep("tripDuration"));
  }, [dispatch]);

  const goNext = useCallback(() => {
    dispatch(setCurrentStep("userPreferences"));
  }, [dispatch]);

  return (
    <>
      <div className="trip-builder-container">
        <div className="group-composition">
          <TextHeadingWithBody
            headingText={t("screens.tripBuilder.groupComposition.heading")}
            bodyText={t("screens.tripBuilder.groupComposition.subheading")}
            headingSize="Xlarge"
            bodySize="Large"
            align="center"
            gap={8}
          />
          <TabSingleSelect
            tabs={travelingWithTabs}
            activeTab={travelingWith}
            onTabSelect={handleTravelingWithSelect}
            variant="outline"
            flex={true}
            testID="traveling-with-selection"
          />
          {travelingWith === "group" && (
            <div className="kids-selection">
              <Text variant="fontHeadingSmall">
                {t("screens.tripBuilder.groupComposition.kids")}
              </Text>
              <TabSingleSelect
                tabs={kidsOptions}
                activeTab={hasKids}
                onTabSelect={handleHasKidsSelect}
                variant="outline"
                flex={true}
                testID="kids-selection"
              />
            </div>
          )}
        </div>
      </div>
      <ModalNavFooter
        onBack={goBack}
        onNext={goNext}
        nextDisabled={groupComposition === null}
      />
    </>
  );
};

export { TripBuilderGroupComposition };
